import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { testIds } from "@/testIds";
import { TOOLBOX_INSERT_EVENT } from "@/components/toolbox/insertHandler";
import type { TableData } from "@/components/editor/table-grid/tableModel";
import { figureColoursUrl, parseFigureColours, statusText, type FigureColours } from "@/lib/tikzAuthoring";
import { isPlotProblem, legendCornerOf, plotSource, problemText, type PlotSpec } from "@/lib/tikzPlot";
import { useTikzDraft } from "@/components/editor/nodeviews/tikz/useTikzDraft";
import { PlotSettings } from "./PlotSettings";
import styles from "./PlotDialog.module.scss";

const T = testIds.plot;

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

export interface PlotDialogProps {
  fetchWithAuth: Fetch;
  documentId: string;
  tableBlockId: string;
  table: TableData;
  /** "Table 2", or "a table" when it has no caption. */
  tableName: string;
  /** Where to start: the suggestions (README-step4, *What it suggests*). */
  initial: PlotSpec;
  onClose: () => void;
}

/** The figure Insert plot adds right after the table: plain TikZ, with its plot spec. */
export function insertPlotFigure(tableBlockId: string, source: string, spec: PlotSpec, caption: string) {
  window.dispatchEvent(
    new CustomEvent(TOOLBOX_INSERT_EVENT, {
      detail: {
        call: { type: "figure", content: { kind: "tikz", source, caption, alt: "", plot: spec } },
        source: "toolbox",
        afterBlockId: tableBlockId,
        openSplit: false,
      },
    }),
  );
}

/**
 * Plot this table (README-step4, 1b): the settings on the left, the drawing on
 * the paper on the right, previewing live. Settings are clicks, so a click
 * redraws at once (the old drawing stays at 60 % for the ~0.7 s it takes; a
 * newer click cancels the older draw); typing a label waits for the pause.
 * The preview draws on the table's own block, before the figure exists.
 */
export function PlotDialog({ fetchWithAuth, documentId, tableBlockId, table, tableName, initial, onClose }: PlotDialogProps) {
  const [spec, setSpec] = useState<PlotSpec>(initial);
  const [colours, setColours] = useState<FigureColours>({});
  const source = useMemo(() => plotSource(table, spec, tableName), [table, spec, tableName]);
  const problem = isPlotProblem(source) ? source : null;
  const text = problem ? "" : (source as string);
  const draft = useTikzDraft({ fetchWithAuth, documentId, blockId: tableBlockId, source: text });
  const drawNow = useRef(draft.drawNow);
  drawNow.current = draft.drawNow;

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const res = await fetchWithAuth(figureColoursUrl(documentId, tableBlockId));
        if (res.ok && live) setColours(parseFigureColours(await res.json()));
      } catch {
        // No swatches: the names still say which colour.
      }
    })();
    return () => {
      live = false;
    };
  }, [fetchWithAuth, documentId, tableBlockId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const change = (next: PlotSpec, immediate: boolean) => {
    setSpec(next);
    if (!immediate) return;
    const src = plotSource(table, next, tableName);
    if (!isPlotProblem(src)) drawNow.current(src);
  };

  const insert = () => {
    if (problem) return;
    insertPlotFigure(tableBlockId, text, spec, table.caption);
    onClose();
  };

  const lines = text ? text.split("\n").length : 0;
  const status = problem
    ? "Nothing to draw"
    : statusText({ state: draft.state, drawMs: draft.drawMs, elapsedMs: draft.elapsedMs, line: draft.error?.line ?? null });

  return createPortal(
    <div className={styles.backdrop} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-label={`Plot ${tableName}`} data-testid={T.dialog}>
        <header className={styles.header}>
          <h3 className={styles.title}>Plot {tableName}</h3>
          <span className={styles.sub}>
            {table.caption ? `${table.caption} · ` : ""}
            {table.rows.length} {table.rows.length === 1 ? "row" : "rows"}
          </span>
          <button type="button" className={styles.close} aria-label="Close" data-testid={T.cancel} onClick={onClose}>
            ×
          </button>
        </header>
        <div className={styles.body}>
          <div className={styles.left}>
            <PlotSettings table={table} spec={spec} onChange={change} colours={colours} legendCorner={text ? legendCornerOf(text) : null} />
          </div>
          <div className={styles.right} data-testid={T.preview}>
            <div className={styles.previewBar}>
              <strong>Preview</strong>
              <span className={styles.sub}>as it prints</span>
              <span className={styles.status} data-testid={T.previewStatus} data-state={problem ? "error" : draft.state}>
                <span className={styles.dot} aria-hidden="true" />
                {status}
              </span>
            </div>
            <div className={styles.paper}>
              {problem ? (
                <p className={styles.problem}>{problemText(problem, tableName)}</p>
              ) : draft.svg ? (
                <img src={draft.svg} alt={`Plot of ${tableName}`} className={styles.drawing} data-dim={draft.state === "drawing" ? "true" : undefined} />
              ) : (
                <div className={styles.placeholder}>Drawing…</div>
              )}
              {table.caption && <p className={styles.caption}>{table.caption}</p>}
            </div>
          </div>
        </div>
        <footer className={styles.footer}>
          <span className={styles.sub}>
            {lines ? `Writes ${lines} lines of pgfplots · ` : ""}follows {tableName} · inserted after it
          </span>
          <button type="button" className={styles.secondary} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={styles.primary} data-testid={T.insert} disabled={!!problem} onClick={insert}>
            Insert plot
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
